import { useState, useMemo, useCallback } from "react";
import { CardElement } from "@stripe/react-stripe-js";
import {
  setPaymentError,
  setPaymentIsProcessing,
  setPaymentSuccess,
  setSurchargeData,
  setShowSurchargeConfirmationModal,
  useConfig,
  useSurcharge,
  useTicket,
  setCardData,
} from "../../../store/Store";
import ApiWrapper from "../../general/ApiWrapper";
import {
  calculateSurcharge,
  addSurchargeToTicketAdjustments,
  removeSurchargeFromTicketAdjustments,
  cancelPaymentIntent,
  isSurchargeApplicable,
  isSurchargeRatesV2Enabled,
  resolveCardFundingType,
} from "../../surcharge/api";
import {
  createStripePaymentSession,
  processStripeExistingCard,
} from "../utils/paymentHelpers";
import { getBalanceDue, fetchTicketData } from "../../general/AppHelpers";
import { paymentFailedStatuses } from "../../general/constants";

export const useStripePayment = ({
  stripe,
  elements,
  selectedPaymentMethod,
  t,
}) => {
  const { accessToken } = useConfig();
  const { data: ticketData } = useTicket();
  const { showSurchargeConfirmationModal } = useSurcharge();

  const [isProcessingNewCard, setIsProcessingNewCard] = useState(false);
  const [stripePaymentIntent, setStripePaymentIntent] = useState(null);

  const isSurchargeApplicableMemoized = useMemo(
    () => isSurchargeApplicable(ticketData),
    [ticketData]
  );

  const isSurchargeRatesV2EnabledMemoized = useMemo(
    () => isSurchargeRatesV2Enabled(ticketData),
    [ticketData]
  );

  const handleSurcharge = async (accessToken) => {
    if (!isSurchargeApplicableMemoized) return {};
    const amount = getBalanceDue(ticketData);
    const [credit, debit] = await Promise.all([
      calculateSurcharge(accessToken, "credit", null, amount),
      calculateSurcharge(accessToken, "debit", null, amount),
    ]);
    return { credit, debit };
  };

  const handleStripeError = async (error) => {
    setPaymentError({
      type: error?.type,
      message: error.message,
    });
    setPaymentIsProcessing(false);
    setIsProcessingNewCard(false);
    setSurchargeData(null);
    setCardData(null);
    setShowSurchargeConfirmationModal(false);
    if (isSurchargeApplicableMemoized)
      removeSurchargeFromTicketAdjustments(ticketData);

    await cancelIntent();
  };

  const proceedToPayment = useCallback(
    async (intent) => {
      const paymentIntent = intent || stripePaymentIntent;
      setPaymentIsProcessing(true);

      const params = {};
      const { deposit } = ticketData?.access_token?.data?.payload;
      const payload = {
        payment_intent: paymentIntent,
        deposit,
      };

      if (isSurchargeApplicableMemoized) {
        if (isSurchargeRatesV2EnabledMemoized) {
          params.card_type = resolveCardFundingType(paymentIntent?.payment_method?.card);
          params.card_country = paymentIntent?.payment_method?.card?.country;
        } else {
          params.card_type = paymentIntent?.payment_method?.card?.funding;
        }
        payload.amount_without_surcharge = getBalanceDue(ticketData);
      }

      const { data, error } = await ApiWrapper.post(
        "/api/mkp/v1/intake/transactions",
        payload,
        { accessToken, params }
      );

      const status = data?.transaction?.status;
      if (error || paymentFailedStatuses.includes(status)) {
        handleStripeError({
          type: error?.type,
          message: error?.message || t("payment.declined"),
        });
      } else {
        setPaymentSuccess(t("payment.success_confirmation"));
        fetchTicketData(accessToken);
      }

      setPaymentIsProcessing(false);
      setSurchargeData(null);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stripePaymentIntent, ticketData]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || (!elements && !selectedPaymentMethod)) return;

    const paymentAmount = getBalanceDue(ticketData);

    if (selectedPaymentMethod) {
      return processStripeExistingCard({
        accessToken,
        amount: paymentAmount,
        selectedPaymentMethod,
        ticketData,
      });
    }

    // Clear any stale surcharge from previous new-card attempts
    if (isSurchargeApplicableMemoized) removeSurchargeFromTicketAdjustments(ticketData);

    setPaymentError(null);
    setIsProcessingNewCard(true);

    const { resource } = ticketData || {};
    const { payment_account, company, location } = resource || {};

    const { credit, debit } = await handleSurcharge(accessToken);
    const maxSurcharge = Math.max(
      credit?.surcharge?.surcharge_amount || 0,
      debit?.surcharge?.surcharge_amount || 0
    );
    const totalAmount = paymentAmount + maxSurcharge;

    const { data: sessionData, error: sessionError } =
      await createStripePaymentSession({
        company,
        location,
        accessToken,
        amount: totalAmount,
        paymentAccount: payment_account,
        callbackUrl: window.location.href,
      });

    if (sessionError)
      return handleStripeError({ message: sessionError.message });

    const cardElement = elements.getElement(CardElement);
    const { error, paymentIntent: confirmedIntent } =
      await stripe.confirmCardPayment(sessionData.client_secret, {
        payment_method: { card: cardElement },
        expand: ["payment_method"],
      });

    if (error) return handleStripeError({ message: error?.message });

    setStripePaymentIntent(confirmedIntent);

    if (isSurchargeRatesV2EnabledMemoized) {
      const fundingType = resolveCardFundingType(confirmedIntent?.payment_method?.card);
      const cardCountry = confirmedIntent?.payment_method?.card?.country;

      if (isSurchargeApplicableMemoized) {
        const { surcharge: finalSurcharge } = await calculateSurcharge(
          accessToken,
          fundingType,
          cardCountry,
          getBalanceDue(ticketData)
        );
        const finalSurchargeAmount = finalSurcharge?.surcharge_amount || 0;
        let activeIntent = confirmedIntent;

        // PI was authorized with maxSurcharge estimate. If actual surcharge differs,
        // cancel old PI and create a new one with the correct total.
        if (finalSurchargeAmount !== maxSurcharge) {
          try {
            await cancelPaymentIntent(accessToken, confirmedIntent, company?.id);
          } catch (cancelErr) {
            return handleStripeError({
              message: cancelErr?.message || t("payment.processing_error"),
            });
          }
          const { data: newSession, error: newSessionError } =
            await createStripePaymentSession({
              company,
              location,
              accessToken,
              amount: paymentAmount + finalSurchargeAmount,
              paymentAccount: payment_account,
              callbackUrl: window.location.href,
            });
          if (newSessionError) return handleStripeError({ message: newSessionError.message });

          const { error: reErr, paymentIntent: newIntent } =
            await stripe.confirmCardPayment(newSession.client_secret, {
              payment_method: { card: cardElement },
              expand: ["payment_method"],
            });
          if (reErr) return handleStripeError({ message: reErr?.message });

          activeIntent = newIntent;
          setStripePaymentIntent(newIntent);
        }

        if (finalSurcharge?.surcharge_configuration_item) {
          removeSurchargeFromTicketAdjustments(ticketData);
          setCardData(activeIntent?.payment_method?.card);
          addSurchargeToTicketAdjustments(ticketData, finalSurcharge);
          setSurchargeData(finalSurcharge);
          setShowSurchargeConfirmationModal(true);
          setIsProcessingNewCard(false);
        } else {
          setIsProcessingNewCard(false);
          await proceedToPayment(activeIntent);
        }
      } else {
        setIsProcessingNewCard(false);
        await proceedToPayment(confirmedIntent);
      }
    } else {
      const fundingType = confirmedIntent?.payment_method?.card?.funding;
      const chosenSurcharge =
        fundingType === "credit" ? credit?.surcharge : debit?.surcharge;

      if (
        isSurchargeApplicableMemoized &&
        chosenSurcharge.surcharge_configuration_item
      ) {
        removeSurchargeFromTicketAdjustments(ticketData);
        setCardData(confirmedIntent?.payment_method?.card);
        addSurchargeToTicketAdjustments(ticketData, chosenSurcharge);
        setSurchargeData(chosenSurcharge);
        setShowSurchargeConfirmationModal(true);
        setIsProcessingNewCard(false);
      } else {
        setIsProcessingNewCard(false);
        await proceedToPayment(confirmedIntent);
      }
    }
  };

  const handleCancelSurchargeModal = async () => {
    elements?.getElement(CardElement)?.clear();
    setShowSurchargeConfirmationModal(false);
    removeSurchargeFromTicketAdjustments(ticketData);
    setSurchargeData(null);

    await cancelIntent();
  };

  const cancelIntent = async () => {
    if (!stripePaymentIntent) return;
    try {
      setIsProcessingNewCard(true);
      await cancelPaymentIntent(
        accessToken,
        stripePaymentIntent,
        ticketData?.resource?.company?.id
      );
    } finally {
      setIsProcessingNewCard(false);
    }
  };

  return {
    handleSubmit,
    handleCancelSurchargeModal,
    proceedToPayment,
    showSurchargeConfirmationModal,
    isProcessingNewCard,
  };
};
